'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from '@/lib/i18n/navigation';
import type { CSSProperties } from 'react';

import type { CMSBanner as CMSBannerType } from '@/lib/cms/types';
import { CMSSection } from '../CmsSection';
import { SectionHeader, type SectionHeaderProps } from '../SectionHeader';

import styles from './Banner.module.scss';

// ============================================
// Props Interface - Adapter Output ONLY
// ============================================

export interface BannerProps extends Omit<CMSBannerType, '__typename' | 'sys' | 'internalName'> {
  /** Optional className */
  className?: string;
  /** CMS index for test IDs */
  cmsIndex?: number;
}

// ============================================
// Helpers
// ============================================

/**
 * Chevron icon for link CTAs
 */
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
    </svg>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * Banner component
 *
 * A flexible banner section with image and text content side-by-side.
 * Supports configurable image position, text alignment, and CTA styles.
 *
 * Accepts ONLY adapter output (CMSBanner type).
 *
 * @example
 * <Banner
 *   heading="Discover Our Collection"
 *   body="Explore our latest watches."
 *   media={{ url: '/banner.jpg', alt: 'Banner' }}
 *   cta={{ text: 'Shop Now', url: '/shop', variant: 'primary' }}
 *   imagePosition="left"
 * />
 */
export function Banner({
  heading,
  body,
  bannerText,
  media,
  cta,
  textColor,
  backgroundColor,
  imagePosition = 'left',
  textAlignment = 'center',
  className,
  cmsIndex,
}: BannerProps) {
  const textAlign = textAlignment || 'center';
  const isReversed = imagePosition === 'right';

  // Get image data directly from adapter output
  const imageUrl = media?.url || null;
  const imageAlt = media?.alt || 'Banner';

  // Render text content - prefer body (plain text) over bannerText (rich text)
  let textContent: React.ReactNode = null;
  if (body) {
    textContent = <p>{body}</p>;
  } else if (bannerText?.json) {
    textContent = documentToReactComponents(bannerText.json as Document);
  }

  const isLinkVariant =
    !cta?.variant || cta.variant.startsWith('link') || cta.variant === 'buttonUnderline';
  const ctaData =
    cta?.url && cta?.text
      ? {
          url: cta.url,
          text: cta.text,
          type: isLinkVariant ? ('link' as const) : ('button' as const),
        }
      : null;

  const wrapperStyle: CSSProperties & Record<string, string> = {};
  if (backgroundColor) {
    wrapperStyle['--banner-bg'] = backgroundColor;
  }

  const testIdPrefix = cmsIndex !== undefined ? `banner-${cmsIndex}` : 'BannerSection';

  return (
    <CMSSection className={className} data-testid={testIdPrefix}>
      <div className={styles.container}>
        <div
          className={clsx(
            styles.wrapper,
            isReversed && styles.wrapperReverse,
            textColor === 'light' && styles.lightText,
            textColor === 'dark' && styles.darkText
          )}
          style={wrapperStyle}
          data-testid="BannerWrapper"
        >
          {imageUrl && (
            <div className={styles.imageContainer}>
              <Image
                src={imageUrl}
                alt={imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className={styles.image}
                data-testid="BannerImage"
              />
            </div>
          )}

          <div
            className={clsx(
              styles.content,
              styles[`content${textAlign.charAt(0).toUpperCase()}${textAlign.slice(1)}`]
            )}
            data-testid="BannerContent"
          >
            {heading && (
              <SectionHeader
                text={heading}
                textAlign={textAlign as SectionHeaderProps['textAlign']}
                sizeDesktop={50}
                sizeMobile={40}
                disableGutters
              />
            )}

            {textContent && (
              <div className={styles.text} data-testid="BannerText">
                {textContent}
              </div>
            )}

            {ctaData &&
              (ctaData.type === 'button' ? (
                <Link
                  href={ctaData.url}
                  className={clsx(styles.cta, styles.ctaButton)}
                  data-testid="BannerCTAButton"
                >
                  {ctaData.text}
                </Link>
              ) : (
                <Link
                  href={ctaData.url}
                  className={clsx(styles.cta, styles.ctaLink)}
                  data-testid="BannerCTALink"
                >
                  {ctaData.text}
                  <ChevronIcon className={styles.chevron} />
                </Link>
              ))}
          </div>
        </div>
      </div>
    </CMSSection>
  );
}

Banner.displayName = 'Banner';
